import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, FormControl, FormArray, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { map, Observable, startWith, Subject, Subscription, takeUntil } from 'rxjs';
import { Profile } from 'src/app/models/Profile';
import { User, receivesRechargeBonus } from 'src/app/models/User';
import { TwoFactorProvision } from 'src/app/models/Token';
import { ProfileService } from '../../../shared/services/profile.service';
import { ToastService } from './../../../shared/components/toast/toast.service';
import { SwitcherComponent } from './../../../shared/switcher/switcher.component';
import { UserService } from './../../../shared/services/user.service';
import { InputCompareValidator } from './../../../shared/validators/input-compare.validator';
import { AGENCY_CLIENT, CLIENT, PARTNER_CLIENT, ADMINISTRATOR } from '../../../shared/services/user.service';
import {ShortenerService} from '../../../shared/services/shortener.service';
import {ProviderAccount} from '../../../models/ProviderAccount';
import { Shortener } from '../../../models/Shortener';
import { UtilService } from 'src/app/shared/services/util.service'
import { FractionationRoute } from './../../../models/Fractionation';
import { FractionationService } from 'src/app/shared/services/fractionations.service';
import { IntegrationTemplateAdminService } from 'src/app/shared/services/admin/integration-template-admin.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { UserRcsSettingsComponent } from '../../admin-rcs/user-rcs-settings/user-rcs-settings.component';
import { RcsUserFields } from '../../admin-rcs/admin-rcs.models';

/** Teto do limite de integrações, o mesmo `max` da validação do backend (UpdateUserRequest). */
export const MAX_WHATSAPP_ACCOUNTS = 50;

/**
 * Flags das N integrações de WhatsApp (CU-17tm466mfzu) no corpo do PUT: só o administrador
 * manda — para os demais os campos nem vão, e o backend mantém o valor atual (o
 * `UpdateUserRequest` recusaria a mudança). O limite fica entre 1 e 50.
 */
export function whatsappAccountsFlags(
  isAdmin: boolean,
  multiAccounts: boolean,
  maxAccounts: unknown,
  loadedMax?: number | null,
): Partial<User> {
  if (!isAdmin) {
    return {};
  }

  // Campo apagado para redigitar e Salvar no meio: fica o limite que estava, não 1 em silêncio.
  const typed = maxAccounts === '' || maxAccounts === null || maxAccounts === undefined ? null : Number(maxAccounts);
  const base = typed === null || Number.isNaN(typed) ? (loadedMax ?? 3) : typed;

  return {
    whatsapp_multi_accounts: !!multiAccounts,
    max_whatsapp_accounts: Math.min(MAX_WHATSAPP_ACCOUNTS, Math.max(1, Math.round(base))),
  };
}

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
  // Classe raiz do estilo global do Admin RCS (`src/_admin-rcs.scss`).
  host: { class: 'rcs-admin' },
})
export class UserComponent implements OnInit, OnDestroy {

  get title(): string {
    if (this.userId) {
      return 'Alterar Usuário';
    }

    return 'Novo usuário';
  }

  userId: string;

  /** Gravação em curso — trava e gira o botão Salvar. */
  isSaving = false;
  active: boolean;
  massiveSmsEnabled: boolean;
  integrationSmsEnabled: boolean;
  postbackTokenRequired: boolean;
  multiuserEnabled = false;
  /** CU-17tm466tzkz — "Recebe bônus de recarga". Ligada por padrão. */
  rechargeBonusEnabled = true;
  /** N integrações de WhatsApp (CU-17tm466mfzu) — só o admin vê e altera. */
  whatsappMultiAccounts = false;
  maxWhatsappAccounts: number | string = 3;
  private loadedMaxWhatsappAccounts: number | null = null;
  revisionSmsEnabled: boolean;
  contentTagEnabled: boolean;
  billingEnabled: boolean;
  tokenVerifyEnabled: boolean;
  partnersAPIEnabled: boolean;
  aiEnabled: boolean;
  canProposeIntegrations: boolean = false;
  exportLeads: boolean;
  whatsappEnabled: boolean;
  analyticsSync: boolean;
  analyticsSalesSync: boolean;
  fakeRequest: boolean;
  deliveryReportEnabled: boolean;
  funnelReportEnabled: boolean;
  disableUtmSource: boolean;
  showFunnelReport: boolean;
  showSalesReport: boolean;
  broadcastSendInterval: boolean;
  generatePassword: boolean;
  pixPriority: boolean;
  operationType: string;
  creditPaymentEnabled: boolean;
  userForm: UntypedFormGroup;
  profiles$: Observable<Profile[]>;
  shorteners$: Observable<Shortener[]>;

  unsubscribe = new Subject<void>();

  operationTypes: any[] = [];

  currentMonitoredPhone: string[];

  fractionation_group_id: string;
  fractionationGroups: FractionationRoute[] = [];
  filteredAutomationFractionationGroupsOptions: Observable<FractionationRoute[]>;
  filteredBroadcastFractionationGroupsOptions: Observable<FractionationRoute[]>;

  get automationFractionationControl() {
    return this.userForm.get('automation_fractionation_route') as FormControl
  }

  get broadcastFractionationControl() {
    return this.userForm.get('broadcast_fractionation_route') as FormControl
  }

  get showCommission() {
    const profileId = this.userForm.get('profile_id')?.value;
    return profileId === AGENCY_CLIENT || profileId === PARTNER_CLIENT;
  }

  /**
   * Perfis que podem ter a API de Parceiros liberada. Espelha
   * Profile::apiCapableProfiles() no backend — se divergir, o admin liga o
   * switch e o próximo login do cliente apaga a flag.
   *
   * Separado de `showCommission` de propósito: comissão e API são regras
   * diferentes, e cliente comum tem API mas não tem comissão.
   */
  /** Estado atual da conta editada; `twoFactorEnabled` é o que o admin manipula. */
  @ViewChild('twoFactorSwitcher') twoFactorSwitcher: SwitcherComponent;

  /** Bloco RCS (17tm466frtx): sabe quais rotas RCS estão desativadas e recarrega a prévia. */
  @ViewChild(UserRcsSettingsComponent) rcsSettings: UserRcsSettingsComponent;

  twoFactorWasEnabled = false;
  twoFactorEnabled = false;
  twoFactorBusy = false;
  twoFactorConfirmingDisable = false;
  twoFactorConfirmingReset = false;
  /** Chave recém-gerada. Vive só na tela: recarregar a página a perde. */
  twoFactorProvision: TwoFactorProvision = null;

  get showApiToggle() {
    const profileId = this.userForm.get('profile_id')?.value;
    return profileId === AGENCY_CLIENT || profileId === PARTNER_CLIENT || profileId === CLIENT;
  }

  /**
   * A flag de segundo fator (CU-86ak84mj6) só aparece editando um usuário
   * existente e só para administrador: desligar o segundo fator de outra pessoa
   * reduz a proteção dela, e é a operação mais sensível desta tela. O backend
   * recusa de qualquer forma — isto evita mostrar um controle que daria 401.
   */
  get showTwoFactorToggle(): boolean {
    return !!this.userId
      && this.authService.getUser(true)?.profile_id === ADMINISTRATOR;
  }

  /**
   * Exigir o token nos postbacks (CU-86ak8p7w0) é decisão de administrador:
   * ligar para um cliente antigo derruba toda integração dele que ainda usa URL
   * sem token. Só em conta existente — a conta nova nasce com o padrão do backend.
   */
  get showPostbackTokenToggle(): boolean {
    return !!this.userId
      && this.authService.getUser(true)?.profile_id === ADMINISTRATOR;
  }

  /**
   * Multiusuários (CU-17tm466h1cm) é liberado conta a conta pelo administrador:
   * cada membro é mais uma pessoa com acesso completo à conta. Só em conta
   * existente: a conta nova nasce sem a liberação.
   */
  get showMultiuserToggle(): boolean {
    return !!this.userId
      && this.authService.getUser(true)?.profile_id === ADMINISTRATOR
      && this.editedProfileCanHaveMembers;
  }

  /** Perfis que podem ter membros: o mesmo recorte de perfis de `User::canHaveAccountMembers` no backend (que também exige a flag). */
  get editedProfileCanHaveMembers(): boolean {
    const profileId = this.userForm.get('profile_id')?.value;
    return profileId === CLIENT || profileId === AGENCY_CLIENT || profileId === PARTNER_CLIENT;
  }

  /**
   * "Recebe bônus de recarga" (CU-17tm466tzkz): hoje a mesma regra do postback token
   * (admin editando conta existente), com nome próprio para uma mudança numa não levar a outra.
   */
  get showRechargeBonusToggle(): boolean {
    return this.showPostbackTokenToggle;
  }

  get monitoredNumbers(): FormArray {
    return this.userForm.get('monitoredNumbers') as FormArray;
  }

  get user() {
    return this.userForm.getRawValue() as User;
  }

  get currentMonitoredPhoneControl(): FormControl {
    return this.userForm.get('currentMonitoredPhone') as FormControl;
  }

  get passwordControl(): FormControl {
    return this.userForm.get('password') as FormControl;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private shortenerService: ShortenerService,
    private profileService: ProfileService,
    private toastService: ToastService,
    private UtilService: UtilService,
    private fractionationService: FractionationService,
    private authService: AuthService,
    private integrationEventAdminService: IntegrationTemplateAdminService,
  ) { }

  get isAdministrator(): boolean {
    return this.authService.getLoggedInUser()?.profile_id === ADMINISTRATOR;
  }

  /**
   * Espelha a permissão de GET /users/{id}/edit-logs no backend
   * (just.admin.can.access): sem isso o botão de histórico apareceria para quem
   * só levaria 401 ao clicar. Customer success fica de fora de propósito — ele
   * edita contas e portanto aparece como autor na própria trilha.
   *
   * `getLoggedInUser()` e não `getUser()`: o backend decide pelo JWT, ou seja
   * pelo operador real — enquanto ele simula uma conta, `getUser()` devolveria
   * o perfil do simulado e o botão sumiria sem motivo.
   */
  get canSeeEditLogs(): boolean {
    return this.authService.getLoggedInUser()?.profile_id === ADMINISTRATOR;
  }

  toggleProposeIntegrations(checked: boolean): void {
    if (!this.userId) {
      return;
    }
    this.integrationEventAdminService.togglePropose(this.userId, checked).subscribe((res) => {
      this.canProposeIntegrations = res.can_propose_integrations;
      this.toastService.success(
        res.can_propose_integrations
          ? 'Permissão para propor eventos de integração concedida.'
          : 'Permissão para propor eventos de integração revogada.'
      );
    });
  }

  ngOnInit(): void {
    this.userForm = this.fb.group({
      id: [''],
      name: ['', [Validators.required, Validators.minLength(5)]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required]],
      commission: ['', null],
      document: ['', [Validators.required, this.cpfValidator]],
      profile_id: ['', [Validators.required]],
      password: ['', null],
      pix_key: ['', null],
      pix_priority: ['', null],
      automation_provider_account_id: ['', null],
      broadcast_provider_account_id: ['', null],
      shortener_id: ['', null],
      password_confirmation: ['', null, InputCompareValidator.compare('password')],
      administrator: [false],
      analytics_sync: [false],
      analytics_sales_sync: [false],
      operation_type: [''],
      credit_payment_enabled: [false],
      massive_sms_enabled: [false],
      integration_sms_enabled: [false],
      content_tag_enabled: [false],
      monitoredNumbers: this.fb.array([], this.validateNumbersArray()),
      currentMonitoredPhone : [''],
      fractionation_group_id: [''],
      automation_fractionation_route: ['', Validators.required],
      broadcast_fractionation_route: ['', Validators.required],
      revision_sms_enabled: [false],
      is_billable: [false],
      sms_token_validated: [false],
      api_enabled: [false],
      ai_enabled: [false],
      unit_send_cost: [null],
      whatsapp_enabled: [false],
      unit_cost_sms: [null],
      // RCS (17tm466frtx): flag e as duas rotas, no padrão das rotas de SMS. Vão no
      // `getRawValue()` do salvar e voltam no `patchValue(user)` da carga.
      rcs_enabled: [false],
      broadcast_rcs_fractionation_route: [null],
      automation_rcs_fractionation_route: [null],
      // Responsável comercial (acompanhamento de agentes, 17tm466frtx): só de LEITURA aqui —
      // `patchValue(user)` preenche com o que o backend devolve no detalhe do usuário, mas quem
      // grava é o `UserRcsSettingsComponent` (botão próprio, rota dedicada
      // `PUT /admin/rcs/clients/{id}/account-manager`). Fica no formulário, e não só numa
      // variável solta, porque é daqui que o componente filho lê o valor salvo (`form.get(...)`).
      account_manager_id: [null]
    });

    this.loadFractionationGroup();

    // Defaults ligados ao criar um novo usuário (sobrescritos pelos valores reais ao editar)
    this.analyticsSalesSync = true;
    this.funnelReportEnabled = true;
    this.deliveryReportEnabled = true;
    this.showFunnelReport = true;

    this.passwordControl
        .valueChanges
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(() => this.userForm.patchValue({ password_confirmation: '' }));

    this.route.params
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(params => {
        this.userId = params['id'];
        if (params['id']) {
          this.userService
            .findById(params['id'])
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(user => {
              this.userForm.patchValue(user);
              this.userId = user.id;
              this.twoFactorWasEnabled = !!user.two_factor_confirmed_at;
              this.twoFactorEnabled = this.twoFactorWasEnabled;
              this.active = user.active;
              this.creditPaymentEnabled = user.credit_payment_enabled;
              this.analyticsSync = user.analytics_sync;
              this.analyticsSalesSync = user.analytics_sales_sync;
              this.fakeRequest = user.fake_request;
              this.deliveryReportEnabled = user.delivery_report_enabled;
              this.funnelReportEnabled = user.funnel_report_enabled;
              this.disableUtmSource = user.disable_utm_source;
              this.showFunnelReport = user.show_funnel_report;
              this.showSalesReport = user.show_sales_report;
              this.exportLeads = user.leads_export;
              this.broadcastSendInterval = user.broadcast_send_interval;
              this.pixPriority = user.pix_priority;
              this.massiveSmsEnabled = user.massive_sms_enabled;
              this.integrationSmsEnabled = user.integration_sms_enabled;
              this.postbackTokenRequired = !!user.postback_token_required;
              this.multiuserEnabled = !!user.multiuser_enabled;
              this.rechargeBonusEnabled = receivesRechargeBonus(user.recharge_bonus_enabled);
              this.applyWhatsappAccountsFlags(user);
              this.revisionSmsEnabled = user.revision_sms_enabled;
              this.contentTagEnabled = user.content_tag_enabled;
              this.billingEnabled = user.is_billable;
              this.tokenVerifyEnabled = user.sms_token_validated;
              this.partnersAPIEnabled = user.api_enabled;
              this.aiEnabled = !!user.ai_enabled;
              this.canProposeIntegrations = !!user.can_propose_integrations;
              this.UtilService.setExportLeads(this.userId, this.exportLeads);

              // Limpar o FormArray antes de adicionar novos controles
              while (this.monitoredNumbers.length !== 0) {
                this.monitoredNumbers.removeAt(0);
              }
              // Adicionar novos controles para cada telefone monitorado
              user.monitored_phones.forEach(item => {
                this.monitoredNumbers.push(new FormControl(item.phone_number, Validators.pattern(/^\+?\d{10,15}$/)));
              });

              this.patchFractionationControls();
          });
        }
      });

    this.profiles$ = this.profileService.listAll();
    this.shorteners$ = this.shortenerService.listAll();
    this.userService.getOperationTypes().subscribe(operation_types => this.operationTypes = operation_types);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  displayFractionationGroupFn(fractionation: FractionationRoute): string {
    return fractionation ? fractionation.name : '';
  }

  private filterValues(value: string | FractionationRoute): FractionationRoute[] {
    let filterValue = ''

    if (typeof value === 'string') {
      filterValue = value?.toLowerCase();
    } else {
      filterValue = value.name?.toLowerCase();
    }

    return this.fractionationGroups.filter(option => option.name.toLowerCase().includes(filterValue));
  }

  updateAutomationFractionationGroupsFilter() {
    const control = this.automationFractionationControl;
    if (control && control.valueChanges){
      this.filteredAutomationFractionationGroupsOptions = control.valueChanges.pipe(
        startWith(''),
        map(value => this.filterValues(value))
      );
    }
  }

  updateBroadcastFractionationGroupsFilter() {
    const control = this.broadcastFractionationControl;
    if (control && control.valueChanges){
      this.filteredBroadcastFractionationGroupsOptions = control.valueChanges.pipe(
        startWith(''),
        map(value => this.filterValues(value))
      );
    }
  }

  validateNumbersArray(): ValidatorFn {
    return (formArray: AbstractControl) => {
      const invalidNumbers = (formArray as FormArray).controls.filter(control =>
        !/^\+?\d{10,15}$/.test(control.value)
      );
      return invalidNumbers.length > 0 ? { invalidNumbers: true } : null;
    };
  }


  addMonitoredNumber(): void {
    const phone = this.userForm.get('currentMonitoredPhone')?.value.replace(/\D/g, "");
    if(phone.length < 11){
      this.toastService.info('Número inválido, o numero precisa ter 11 digitos.');
      return;
    }

    const alreadyExists = this.monitoredNumbers.controls.some(ctrl => ctrl.value === phone);
    if (alreadyExists) {
      // Display a toast message if the number already exists
      this.toastService.info('Número já cadastrado.');
      return;
    }

    // Add the number if it does not exist
    this.monitoredNumbers.push(new FormControl(phone));
    this.userForm.get('currentMonitoredPhone')?.setValue(''); // Clear input
  }

  removeMonitoredNumber(index: number): void {
    this.monitoredNumbers.removeAt(index);
  }


  toggleGeneratePassword(): void {
    const newPassword = this.generatePassword ? 'GENERATED_PASSWORD' : '';

    this.userForm.patchValue({
      password: newPassword,
      password_confirmation: newPassword
    });
  }

  onSwitchChange(value: boolean): void {
    this.exportLeads = value;
    this.UtilService.setExportLeads(this.userId, value)
  }

  loadFractionationGroup(): void {
    this.fractionationService.getRoutes(1, 9999).subscribe({
      next: (data: FractionationRoute[]) => {
        this.fractionationGroups = data;
        this.updateAutomationFractionationGroupsFilter();
        this.updateBroadcastFractionationGroupsFilter();
        this.patchFractionationControls();
      },
      error: (error) => {
        this.toastService.error('Erro ao buscar as rotas');
      }
    });
  }

  patchFractionationControls(): void {
    if (this.fractionationGroups?.length === 0) {
      return;
    }

    const automationRoute = this.fractionationGroups.find(route => route.id === this.automationFractionationControl?.value) || null;
    const broadcastRoute = this.fractionationGroups.find(route => route.id === this.broadcastFractionationControl?.value) || null;

    this.userForm.patchValue({ automation_fractionation_route: automationRoute });
    this.userForm.patchValue({ broadcast_fractionation_route: broadcastRoute });
  }

  onAutomationControlBlur(): void {
    const automationRouteId = this.automationFractionationControl?.value;
    if (typeof automationRouteId !== 'object') {
      this.userForm.patchValue({ automation_fractionation_route: null });
    }
  }

  onBroadcastControlBlur(): void {
    const broadcastRouteId = this.broadcastFractionationControl?.value;
    if (typeof broadcastRouteId !== 'object') {
      this.userForm.patchValue({ broadcast_fractionation_route: null });
    }
  }

  formatCpf(event: any) {
    let value = event.target.value;

    // Remove qualquer caractere que não seja número
    value = value.replace(/\D/g, '');

    // Limita a quantidade de dígitos a 11
    if (value.length > 11) {
      value = value.substring(0, 11);
    }

    // Aplica a formatação ###.###.###-##
    if (value.length <= 11) {
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }

    // Atualiza o valor formatado no input
    event.target.value = value;
  }

  cpfValidator(control: AbstractControl): ValidationErrors | null {
    const cpf = control.value?.replace(/\D/g, '');

    if (!cpf || cpf.length !== 11) {
      return { cpfInvalid: true };
    }

    let sum = 0;
    let remainder;

    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1{10}$/.test(cpf)) {
      return { cpfInvalid: true };
    }

    // Valida o primeiro dígito verificador
    for (let i = 1; i <= 9; i++) {
      sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;

    if (remainder === 10 || remainder === 11) {
      remainder = 0;
    }
    if (remainder !== parseInt(cpf.substring(9, 10))) {
      return { cpfInvalid: true };
    }

    // Valida o segundo dígito verificador
    sum = 0;
    for (let i = 1; i <= 10; i++) {
      sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
    }
    remainder = (sum * 10) % 11;

    if (remainder === 10 || remainder === 11) {
      remainder = 0;
    }
    if (remainder !== parseInt(cpf.substring(10, 11))) {
      return { cpfInvalid: true };
    }

    return null; // CPF válido
  }

  onFunnelReportToggle(enabled: boolean): void {
    if (enabled) {
      this.analyticsSalesSync = true;
      this.deliveryReportEnabled = true;
    }
  }

  onFunnelDependencyToggle(): void {
    if (!this.analyticsSalesSync || !this.deliveryReportEnabled) {
      this.funnelReportEnabled = false;
      this.showFunnelReport = false;
    }
  }

  /**
   * O segundo fator age na hora, e não no Salvar.
   *
   * Ligar devolve a chave uma única vez — se dependesse do Salvar, um cancelar
   * no meio deixaria a conta com um segredo que ninguém mais consegue ler.
   * Desligar pede confirmação porque invalida o aplicativo já cadastrado.
   */
  onTwoFactorToggle(checked: boolean): void {
    if (checked && !this.twoFactorEnabled) {
      this.provisionTwoFactor();
      return;
    }

    if (!checked && this.twoFactorEnabled) {
      // O switch só volta para OFF quando a confirmação for aceita.
      this.twoFactorEnabled = true;
      this.syncTwoFactorSwitcher();
      this.twoFactorConfirmingDisable = true;
    }
  }

  /**
   * Gera a chave e a mantém na tela até o admin fechar. Chamar de novo numa
   * conta que já tem segundo fator invalida a chave anterior — é o caminho de
   * "perdi o aparelho", e por isso passa pela mesma confirmação.
   */
  provisionTwoFactor(): void {
    this.twoFactorConfirmingReset = false;
    this.twoFactorBusy = true;

    this.userService
      .enableTwoFactor(this.userId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        provision => {
          this.twoFactorBusy = false;
          this.twoFactorProvision = provision;
          this.twoFactorWasEnabled = true;
          this.twoFactorEnabled = true;
          this.syncTwoFactorSwitcher();
          this.toastService.success(provision.message);
        },
        () => {
          this.twoFactorBusy = false;
          this.twoFactorEnabled = this.twoFactorWasEnabled;
          this.syncTwoFactorSwitcher();
          this.toastService.error('Não foi possível ativar a verificação em duas etapas.');
        }
      );
  }

  confirmDisableTwoFactor(): void {
    this.twoFactorBusy = true;

    this.userService
      .disableTwoFactor(this.userId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        () => {
          this.twoFactorBusy = false;
          this.twoFactorConfirmingDisable = false;
          this.twoFactorProvision = null;
          this.twoFactorWasEnabled = false;
          this.twoFactorEnabled = false;
          this.syncTwoFactorSwitcher();
          this.toastService.success('Verificação em duas etapas desativada para o usuário.');
        },
        () => {
          this.twoFactorBusy = false;
          this.twoFactorConfirmingDisable = false;
          this.syncTwoFactorSwitcher();
          this.toastService.error('Não foi possível desativar a verificação em duas etapas.');
        }
      );
  }

  cancelTwoFactorConfirm(): void {
    this.twoFactorConfirmingDisable = false;
    this.twoFactorConfirmingReset = false;
    this.twoFactorEnabled = this.twoFactorWasEnabled;
    this.syncTwoFactorSwitcher();
  }

  /** Fecha o painel da chave. Depois disto ela não é mais recuperável. */
  dismissTwoFactorProvision(): void {
    this.twoFactorProvision = null;
  }

  copyTwoFactorSecret(): void {
    this.copyToClipboard(this.twoFactorProvision?.secret, 'Chave copiada.');
  }

  copyTwoFactorRecoveryCodes(): void {
    this.copyToClipboard(
      (this.twoFactorProvision?.recovery_codes ?? []).join('\n'),
      'Códigos de recuperação copiados.'
    );
  }

  private copyToClipboard(texto: string, sucesso: string): void {
    if (!texto) {
      return;
    }

    if (!navigator.clipboard?.writeText) {
      this.toastService.error('O navegador não permitiu copiar. Selecione o texto à mão.');
      return;
    }

    navigator.clipboard.writeText(texto).then(
      () => this.toastService.success(sucesso),
      () => this.toastService.error('Não foi possível copiar.')
    );
  }

  /**
   * Flag e limite das N integrações de WhatsApp vindos da conta aberta (CU-17tm466mfzu). Se o
   * switch abrir desligado com a flag ligada no banco, o próximo Salvar a desligaria.
   */
  applyWhatsappAccountsFlags(user: Partial<User>): void {
    this.whatsappEnabled = !!user.whatsapp_enabled;
    this.whatsappMultiAccounts = !!user.whatsapp_multi_accounts;
    this.maxWhatsappAccounts = user.max_whatsapp_accounts ?? 3;
    this.loadedMaxWhatsappAccounts = this.maxWhatsappAccounts;
  }

  /**
   * Quem libera várias integrações de WhatsApp: hoje a mesma regra do postback token (admin
   * editando conta existente), com nome próprio para uma mudança numa não levar a outra.
   */
  get canManageWhatsappAccounts(): boolean {
    return this.showPostbackTokenToggle;
  }

  save(): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      this.toastService.error('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    const user = this.userForm.getRawValue() as User & RcsUserFields;
    user.active = this.active;
    user.credit_payment_enabled = this.creditPaymentEnabled;
    user.pix_priority = this.pixPriority;
    user.massive_sms_enabled = this.massiveSmsEnabled;
    user.integration_sms_enabled = this.integrationSmsEnabled;
    // Só administrador vê o switch; para os demais a flag nem vai no corpo e o
    // backend mantém o valor atual.
    if (this.showPostbackTokenToggle) {
      user.postback_token_required = this.postbackTokenRequired;
    }
    if (this.showRechargeBonusToggle) {
      user.recharge_bonus_enabled = this.rechargeBonusEnabled;
    }
    // Mesma regra: fora do switch a flag não vai no corpo, e o backend mantém
    // o valor atual.
    if (this.showMultiuserToggle) {
      user.multiuser_enabled = this.multiuserEnabled;
    }
    // WhatsApp oficial (CU-17tm466qj38): mesma regra — só quem vê o switch manda a flag. O
    // `whatsapp_enabled` também é controle do formulário (sempre `false`), então para os demais
    // ele sai do corpo: senão o CS salvando uma conta com o oficial ligado mandaria `false`.
    if (this.canManageWhatsappAccounts) {
      user.whatsapp_enabled = this.whatsappEnabled;
    } else {
      delete user.whatsapp_enabled;
    }
    Object.assign(user, whatsappAccountsFlags(
      this.canManageWhatsappAccounts, this.whatsappMultiAccounts, this.maxWhatsappAccounts, this.loadedMaxWhatsappAccounts,
    ));
    user.revision_sms_enabled = this.revisionSmsEnabled;
    user.content_tag_enabled = this.contentTagEnabled;
    user.is_billable = this.billingEnabled;
    user.sms_token_validated = this.tokenVerifyEnabled;
    // Perfil sem direito a API (admin/CS) nunca envia a flag ligada: o backend
    // aceitaria e o próximo login do usuário a revogaria, deixando o painel
    // mostrando um estado que não existe mais.
    user.api_enabled = this.showApiToggle && this.partnersAPIEnabled;
    user.ai_enabled = this.aiEnabled;
    user.analytics_sync = this.analyticsSync;
    user.analytics_sales_sync = this.analyticsSalesSync;
    user.fake_request = this.fakeRequest;
    user.delivery_report_enabled = this.deliveryReportEnabled;
    user.funnel_report_enabled = this.funnelReportEnabled;
    user.show_funnel_report = this.showFunnelReport;
    user.show_sales_report = this.showSalesReport;
    user.broadcast_send_interval = this.broadcastSendInterval;
    user.leads_export = this.exportLeads;
    user.billing_update = true;
    user.disable_utm_source = this.disableUtmSource;

    user.monitored_phones = this.monitoredNumbers.controls.map(control => ({
      user_id: user.id ,
      phone_number: control.value
    }));

    const automationFractionationRoute = this.userForm.get('automation_fractionation_route')?.value;
    const broadcastFractionationRoute = this.userForm.get('broadcast_fractionation_route')?.value;

    if (!broadcastFractionationRoute || broadcastFractionationRoute.active === false ) {
      return this.toastService.error('Rota de broadcast escolhida está desativada');
    }

    if (!automationFractionationRoute || automationFractionationRoute.active === false) {
      return this.toastService.error('Rota de automação escolhida está desativada');
    }

    if (this.rcsSettings?.isInactive('broadcast_rcs_fractionation_route')) {
      return this.toastService.error('Rota RCS de envio em massa escolhida está desativada');
    }

    if (this.rcsSettings?.isInactive('automation_rcs_fractionation_route')) {
      return this.toastService.error('Rota RCS de automação escolhida está desativada');
    }

    user.rcs_enabled = !!this.userForm.get('rcs_enabled')?.value;

    user.broadcast_fractionation_route = this.userForm.get('broadcast_fractionation_route')?.value?.id;
    user.automation_fractionation_route = this.userForm.get('automation_fractionation_route')?.value?.id;

    if (this.fractionationGroups?.length === 0) {
      this.toastService.error('Nenhuma rota cadastrada');
      return;
    }

    const monitoredRoute = this.fractionationGroups?.find(route => route.monitored === true);

    if (!monitoredRoute) {
      // A monitorada é por canal desde a F2.8: só a de SMS conta aqui.
      return this.toastService.error('Nenhuma rota de SMS monitorada. Marque uma em Admin → Fracionamentos, na aba SMS.');
    }

    this.fractionation_group_id = `${monitoredRoute.id}`;

    const successMessage = user.id
      ? 'Usuário alterado com sucesso!'
      : 'Usuário inserido com sucesso!';

    if (user.commission > 12) {
      this.toastService.error('A comissão máxima é 12%.');
      return;
    }

    this.persist(user, successMessage);
  }

  private persist(user: User, successMessage: string): void {
    this.isSaving = true;
    this.userService
      .save(user)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: user => {
            this.isSaving = false;
            let message = successMessage;
            if (user.password) {
              message += `\nNova senha: ${user.password}`;
            }
            this.toastService.success(message);
            // A prévia do RCS lê as rotas salvas: só agora ela reflete o que mudou.
            this.rcsSettings?.loadPreview();
            if (!this.userId) {
              this.router.navigate(['/users/edit', user.id]);
            }
          },
          error: error => {
            this.isSaving = false;
            const errorMessage = error.error?.message || 'Ocorreu um erro ao salvar o usuário.';
            this.toastService.error(errorMessage);
          }
        });
  }


  /** Devolve o switcher ao estado real da conta. */
  private syncTwoFactorSwitcher(): void {
    if (this.twoFactorSwitcher) {
      this.twoFactorSwitcher.checked = this.twoFactorEnabled;
    }
  }
}
